import { Box, LinearProgress } from "@mui/material";
import { alpha } from "@mui/material/styles";

import type { BulkFileState } from "./bulkUpload.js";
import { formatBytes } from "./utils.js";

/** Per-file progress list shared by the Bulk-upload page and the multi-file
 * path of the single Upload page: a count header, a determinate progress bar
 * while running, one row per file with a status pill, and a final summary box.
 * Keeping it in one place stops the two upload surfaces from drifting. */
export function BulkFileList({
  items,
  categoryName,
  running,
  summary,
}: {
  items: readonly BulkFileState[];
  categoryName?: string | null;
  running: boolean;
  summary: { done: number; failed: number } | null;
}): JSX.Element | null {
  if (items.length === 0) return null;
  const doneCount = items.filter((s) => s.status === "done").length;
  const pct = items.length
    ? Math.round((items.filter((s) => s.status !== "pending").length / items.length) * 100)
    : 0;

  return (
    <Box sx={{ display: "grid", gap: 1 }}>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", fontSize: 12, color: "text.secondary" }}>
        <span>
          {items.length} file{items.length === 1 ? "" : "s"}
          {categoryName ? ` → ${categoryName}` : ""}
        </span>
        <span>
          {doneCount}/{items.length} uploaded
        </span>
      </Box>
      {(running || summary) && <LinearProgress variant="determinate" value={pct} sx={{ borderRadius: 1 }} />}
      <Box sx={{ display: "grid", gap: 0.5, maxHeight: 280, overflowY: "auto" }}>
        {items.map((s, i) => (
          <Box
            key={`${s.name}:${i}`}
            data-testid="bulk-file-row"
            sx={{ display: "flex", alignItems: "center", gap: 1.5, borderRadius: 1, border: 1, borderColor: "divider", px: 1.5, py: 0.75 }}
          >
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Box sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontSize: 13, fontWeight: 500 }}>{s.name}</Box>
              <Box sx={{ fontSize: "11px", color: "text.disabled" }}>{formatBytes(s.size)}</Box>
            </Box>
            <BulkStatusPill state={s} />
          </Box>
        ))}
      </Box>
      {summary && (
        <Box
          role="status"
          sx={{
            borderRadius: 1,
            px: 1.5,
            py: 1,
            fontSize: 13,
            bgcolor: (t) => alpha(summary.failed ? t.palette.warning.main : t.palette.success.main, 0.12),
            color: summary.failed ? "warning.main" : "success.main",
          }}
        >
          Uploaded {summary.done} document{summary.done === 1 ? "" : "s"}
          {summary.failed ? ` · ${summary.failed} failed (left in the list to retry)` : ""}
          {categoryName ? ` to ${categoryName}` : ""}.
        </Box>
      )}
    </Box>
  );
}

function BulkStatusPill({ state }: { state: BulkFileState }): JSX.Element {
  const map: Record<BulkFileState["status"], { label: string; color: string }> = {
    pending: { label: "Queued", color: "text.disabled" },
    uploading: { label: "Uploading…", color: "info.main" },
    done: { label: "✓ Uploaded", color: "success.main" },
    error: { label: "✗ Failed", color: "error.main" },
  };
  const { label, color } = map[state.status];
  return (
    <Box sx={{ flexShrink: 0, fontSize: 12, fontWeight: 500, color }} title={state.error ?? undefined}>
      {label}
    </Box>
  );
}
